<template>
	<div :class="bemm()">
		<form :class="bemm('form')" @submit.prevent="updatePassword">
			<FormGroup>
				<InputPassword
					v-model="form.currentPassword"
					:label="$t('forms.labels.currentPassword')"
					:status="fieldError('currentPassword') ? 'error' : ''"
					:error="fieldError('currentPassword')"
					@blur="touchField('currentPassword')"
				/>

				<InputPassword
					v-model="form.newPassword"
					:label="$t('forms.labels.newPassword')"
					:status="newPasswordError ? 'error' : ''"
					:error="newPasswordError"
					@blur="touchField('newPassword')"
				/>
				<Checklist
					:title="$t('forms.passwordChecklist.title')"
					:items="passwordChecklistItems"
					:class="bemm('password-checklist')"
				/>

				<InputPassword
					v-model="form.confirmPassword"
					:label="$t('forms.labels.confirmPassword')"
					:status="fieldError('confirmPassword') ? 'error' : ''"
					:error="fieldError('confirmPassword')"
					@blur="touchField('confirmPassword')"
				/>
			</FormGroup>

			<ButtonGroup :class="bemm('actions')" align="right">
				<Button
					button-type="submit"
					:disabled="formSubmissionInProgress"
					:status="formSubmissionInProgress ? 'loading' : 'default'"
				>
					{{ $t('common.actions.save') }}
				</Button>
			</ButtonGroup>
		</form>
	</div>
</template>

<script lang="ts" setup>
import { computed, reactive, ref } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, password, sameAs } from '@/composables/useValidationRules';
import { maxLength } from '@vuelidate/validators';
import { RepositoryFactory } from '@/repositories';
import { useAuthStore } from '@/stores/auth';
import { useErrorHandler } from '@/mixins/errorHandlerMixin';
import { FormGroup, InputPassword } from '@arritech/ui/Form';
import { Button, ButtonGroup } from '@arritech/ui/Button';
import { Checklist } from '@arritech/ui/Checklist';
import {
	ChecklistStatus,
	type ChecklistItem,
	type ChecklistStatus as ChecklistStatusType,
} from '@arritech/ui/Checklist';
import i18n from '@/i18n';
import { useBemm } from 'bemm';

type PasswordField = 'currentPassword' | 'newPassword' | 'confirmPassword';

const emit = defineEmits<{
	(event: 'close'): void;
}>();

const authStore = useAuthStore();
const { successMsg, handleHttpStatus, errorMsg } = useErrorHandler();
const UserRepository = RepositoryFactory.get('user');
const t = i18n.global.t;
const bemm = useBemm('update-password', {
	return: 'string',
	includeBaseClass: true,
});

const userId = sessionStorage.getItem('user_id');
const formSubmissionInProgress = ref(false);

const form = reactive({
	currentPassword: '',
	newPassword: '',
	confirmPassword: '',
});

const passwordContainsLowercase = /[a-z]/;
const passwordContainsUppercase = /[A-Z]/;
const passwordContainsNumber = /[0-9]/;
const passwordContainsSpecialCharacter = /[!@#\$%\^\&*'\)\(+=._-]/;

const ruleStatus = (
	isValid: boolean,
	hasValue: boolean
): ChecklistStatusType => {
	if (!hasValue) return ChecklistStatus.IDLE;
	return isValid ? ChecklistStatus.SUCCESS : ChecklistStatus.ERROR;
};

const rules = computed(() => ({
	form: {
		currentPassword: { required },
		newPassword: {
			required,
			password,
			maxLength: maxLength(20),
		},
		confirmPassword: {
			required,
			sameAs: sameAs(() => form.newPassword, {
				field1: 'New Password',
				field2: 'Confirm Password',
			}),
		},
	},
}));

const v$ = useVuelidate<{ form: typeof form }>(rules, { form });

const touchField = (field: PasswordField) => {
	const formValidation = v$.value?.form;
	if (!formValidation) return;
	formValidation[field]?.$touch?.();
};

const fieldError = (
	field: PasswordField,
	ignoredValidators: string[] = []
): string => {
	const formValidation = v$.value?.form;
	if (!formValidation) return '';
	const validation = formValidation[field];
	if (!validation) return '';
	const firstMatchingError = validation.$errors.find(
		(error) => !ignoredValidators.includes(String(error.$validator))
	);

	return validation.$dirty && firstMatchingError
		? String(firstMatchingError.$message)
		: '';
};

const newPasswordError = computed(() =>
	fieldError('newPassword', ['password'])
);

const passwordChecklistItems = computed<ChecklistItem[]>(() => {
	const passwordValue = form.newPassword;
	const hasValue = passwordValue.length > 0;

	return [
		{
			id: 'length-min',
			label: t('forms.passwordChecklist.minLength'),
			status: ruleStatus(passwordValue.length >= 10, hasValue),
		},
		{
			id: 'uppercase',
			label: t('forms.passwordChecklist.uppercase'),
			status: ruleStatus(
				passwordContainsUppercase.test(passwordValue),
				hasValue
			),
		},
		{
			id: 'lowercase',
			label: t('forms.passwordChecklist.lowercase'),
			status: ruleStatus(
				passwordContainsLowercase.test(passwordValue),
				hasValue
			),
		},
		{
			id: 'number',
			label: t('forms.passwordChecklist.number'),
			status: ruleStatus(passwordContainsNumber.test(passwordValue), hasValue),
		},
		{
			id: 'special',
			label: t('forms.passwordChecklist.specialCharacter'),
			status: ruleStatus(
				passwordContainsSpecialCharacter.test(passwordValue),
				hasValue
			),
		},
		{
			id: 'length-max',
			label: t('forms.passwordChecklist.maxLength'),
			status: ruleStatus(passwordValue.length <= 20, hasValue),
		},
	];
});

const resetForm = () => {
	form.currentPassword = '';
	form.newPassword = '';
	form.confirmPassword = '';
	v$.value?.$reset?.();
};

const updatePassword = async () => {
	const validationState = v$.value;
	if (!validationState) return;
	const isFormCorrect = await validationState.$validate();
	if (!isFormCorrect || formSubmissionInProgress.value) return;
	if (!userId) {
		errorMsg(t('auth.updatePassword.errorBody'));
		return;
	}

	formSubmissionInProgress.value = true;
	const payload = {
		currentPassword: form.currentPassword,
		newPassword: form.newPassword,
		deviceInfo: navigator.userAgent,
	};

	UserRepository.updatePassword(payload, userId)
		.then((resp) => {
			if (resp.data.success) {
				successMsg(t('auth.updatePassword.success'));
				resetForm();
				authStore.setPasswordNeedsUpdate(false);
				emit('close');
			}
		})
		.catch((err) => {
			handleHttpStatus(
				err,
				t('auth.updatePassword.errorTitle'),
				t('auth.updatePassword.errorBody')
			);
		})
		.finally(() => {
			formSubmissionInProgress.value = false;
		});
};
</script>

<style lang="scss">
.update-password {
	width: 100%;

	&__form {
		display: flex;
		flex-direction: column;
		gap: var(--space-l);
	}

	&__actions {
		width: 100%;
	}

	&__password-checklist {
		margin-top: calc(var(--space-xs) * -1);
	}
}
</style>
